import dayjs, { Dayjs } from "dayjs";
import { StudentReviewGroup } from "./useTeacherStudentReviews.ts";
import { TeacherCoordinatorReviewHistoryItem } from "./useTeacherCoordinatorReviewHistory.ts";
import {
  coordinatorWeightedScore,
  STUDENT_SCORE_WINDOW_MONTHS,
} from "./teacherScoreAlgorithm.ts";
import {
  flattenStudentReviews,
  summarizeStudentReviews,
} from "./studentReviewWindows.ts";
import { coordinatorReviewPeriodStartsBetween } from "./coordinatorReviewPeriod.ts";

type MonthlyPeriod = {
  start: Dayjs;
  end: Dayjs;
  monthsAgo: number;
};

export type TeacherScoreTrendPoint = {
  label: string;
  firstDay: Dayjs;
  lastDay: Dayjs;
  isInScoreWindow: boolean;
  studentAverage: number | null;
  studentReviewCount: number;
  reviewingStudentCount: number;
  coordinatorReviewInForce: TeacherCoordinatorReviewHistoryItem | null;
  coordinatorReviewSubmittedInPeriod: boolean;
};

export type CoordinatorReviewMark = {
  periodAxisPosition: number;
  score: number;
  review: TeacherCoordinatorReviewHistoryItem;
};

export type CoordinatorReviewPeriodStart = {
  periodAxisPosition: number;
  date: Dayjs;
};

export type TeacherScoreTrend = {
  points: TeacherScoreTrendPoint[];
  coordinatorReviewMarks: CoordinatorReviewMark[];
  coordinatorReviewPeriodStarts: CoordinatorReviewPeriodStart[];
};

const EMPTY_TREND: TeacherScoreTrend = {
  points: [],
  coordinatorReviewMarks: [],
  coordinatorReviewPeriodStarts: [],
};

function oldestDate(dates: Dayjs[]): Dayjs | null {
  return dates.reduce<Dayjs | null>(
    (oldest, date) =>
      oldest === null || date.isBefore(oldest) ? date : oldest,
    null,
  );
}

function buildMonthlyPeriodsEndingAt(
  now: Dayjs,
  oldestActivity: Dayjs,
): MonthlyPeriod[] {
  const periods: MonthlyPeriod[] = [];
  let monthsAgo = 0;
  while (
    periods.length < STUDENT_SCORE_WINDOW_MONTHS ||
    periods[0].start.isAfter(oldestActivity)
  ) {
    periods.unshift({
      start: now.subtract(monthsAgo + 1, "month"),
      end: now.subtract(monthsAgo, "month"),
      monthsAgo,
    });
    monthsAgo++;
  }
  return periods;
}

function isCurrentPeriod(period: MonthlyPeriod): boolean {
  return period.monthsAgo === 0;
}

function happenedBeforePeriodEnd(date: Dayjs, period: MonthlyPeriod): boolean {
  return isCurrentPeriod(period) || date.isBefore(period.end);
}

function happenedDuringPeriod(date: Dayjs, period: MonthlyPeriod): boolean {
  return !date.isBefore(period.start) && happenedBeforePeriodEnd(date, period);
}

function periodAxisPositionOf(date: Dayjs, periods: MonthlyPeriod[]): number {
  const periodIndex = periods.findIndex((period) =>
    happenedDuringPeriod(date, period),
  );
  if (periodIndex === -1) return -0.5;

  const period = periods[periodIndex];
  const elapsedShareOfPeriod = Math.min(
    date.diff(period.start) / period.end.diff(period.start),
    1,
  );
  return periodIndex - 0.5 + elapsedShareOfPeriod;
}

function lastOf<T>(items: T[]): T | null {
  return items.length > 0 ? items[items.length - 1] : null;
}

export function buildTeacherScoreTrend({
  studentReviewGroups,
  coordinatorReviews,
  now,
}: {
  studentReviewGroups: StudentReviewGroup[];
  coordinatorReviews: TeacherCoordinatorReviewHistoryItem[];
  now: Dayjs;
}): TeacherScoreTrend {
  const studentReviews = flattenStudentReviews(studentReviewGroups);
  const coordinatorReviewsOldestFirst = [...coordinatorReviews].sort(
    (a, b) =>
      dayjs(a.reviewedAtUtc).valueOf() - dayjs(b.reviewedAtUtc).valueOf(),
  );

  const oldestActivity = oldestDate([
    ...studentReviews.map((review) => review.reviewedAt),
    ...coordinatorReviews.map((review) => dayjs(review.reviewedAtUtc)),
  ]);
  if (!oldestActivity) return EMPTY_TREND;

  const periods = buildMonthlyPeriodsEndingAt(now, oldestActivity);

  const points = periods.map((period) => {
    const periodStudentReviews = summarizeStudentReviews(
      studentReviews.filter((review) =>
        happenedDuringPeriod(review.reviewedAt, period),
      ),
    );
    const coordinatorReviewInForce = lastOf(
      coordinatorReviewsOldestFirst.filter((review) =>
        happenedBeforePeriodEnd(dayjs(review.reviewedAtUtc), period),
      ),
    );
    const lastDay = isCurrentPeriod(period)
      ? period.end
      : period.end.subtract(1, "day");

    return {
      label: period.start.format("D MMM [']YY"),
      firstDay: period.start,
      lastDay,
      isInScoreWindow: period.monthsAgo < STUDENT_SCORE_WINDOW_MONTHS,
      studentAverage: periodStudentReviews.average,
      studentReviewCount: periodStudentReviews.reviewCount,
      reviewingStudentCount: periodStudentReviews.studentCount,
      coordinatorReviewInForce,
      coordinatorReviewSubmittedInPeriod:
        coordinatorReviewInForce !== null &&
        happenedDuringPeriod(
          dayjs(coordinatorReviewInForce.reviewedAtUtc),
          period,
        ),
    };
  });

  const coordinatorReviewMarks = coordinatorReviewsOldestFirst.map(
    (review) => ({
      periodAxisPosition: periodAxisPositionOf(
        dayjs(review.reviewedAtUtc),
        periods,
      ),
      score: coordinatorWeightedScore(review.scores),
      review,
    }),
  );

  const coordinatorReviewPeriodStarts = coordinatorReviewPeriodStartsBetween(
    periods[0].start.toDate(),
    now.toDate(),
  ).map((periodStart) => ({
    periodAxisPosition: periodAxisPositionOf(dayjs(periodStart), periods),
    date: dayjs(periodStart),
  }));

  return { points, coordinatorReviewMarks, coordinatorReviewPeriodStarts };
}
